import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { useAuth } from '../../../../auth/useAuth'
import { useTableSort } from '../../../../hooks/useTableSort'
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader'

// Types
interface Fund {
  Id: number
  Fund: string
  Issuer: string
  Manager_Name: string
  Strategy: string
  Fund_Type: string
  Currency: string
  Management_Fee_Com: number | null
  Management_Fee_Inv: number | null
  Carry: number | null
  Preferred_Return: number | null
  End_Inv_Period: Date | null
  Investment_Period: string
  Harvest_Period: Date | null
  Extension: Date | null
  Total_Fund_Committment: number | null
  Fund_Size: number | null
  Asof: Date | null
  Timestamp: Date
  Deleted: boolean
  CreatedBy?: string
  ModifiedBy?: string
}

interface PaginationInfo {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

interface PaginatedResponse {
  data: Fund[]
  pagination: PaginationInfo
}

const initialFund: Omit<Fund, 'Id' | 'Timestamp'> = {
  Fund: '',
  Issuer: '',
  Manager_Name: '',
  Strategy: '',
  Fund_Type: '',
  Currency: '',
  Management_Fee_Com: null,
  Management_Fee_Inv: null,
  Carry: null,
  Preferred_Return: null,
  End_Inv_Period: null,
  Investment_Period: '',
  Harvest_Period: null,
  Extension: null,
  Total_Fund_Committment: null,
  Fund_Size: null,
  Asof: null,
  Deleted: false
}

const FundsManagement: React.FC = () => {
  const { user, getAccessToken } = useAuth()
  const [funds, setFunds] = useState<Fund[]>([])
  const [strategies, setStrategies] = useState<string[]>([])
  const [fundTypes, setFundTypes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [backgroundLoading, setBackgroundLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  
  // Pagination state
  const [totalCount, setTotalCount] = useState(0)
  const [loadedCount, setLoadedCount] = useState(0)
  const [currentBackendPage, setCurrentBackendPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  
  // Form state
  const [formData, setFormData] = useState<Omit<Fund, 'Id' | 'Timestamp'>>(initialFund)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [appliedFilters, setAppliedFilters] = useState<Omit<Fund, 'Id' | 'Timestamp'>>(initialFund)
  
  // Table state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedStrategy, setSelectedStrategy] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [selectedCurrency, setSelectedCurrency] = useState('')
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [fundToDelete, setFundToDelete] = useState<Fund | null>(null)

  // Identifier state
  const IDENTIFIER_TYPES = ['BEZ', 'CUSIP', 'ISIN', 'LEI']
  const [fundIdType, setFundIdType] = useState<string>('')
  const [fundIdValue, setFundIdValue] = useState<string>('')
  const [issuerIdType, setIssuerIdType] = useState<string>('')
  const [issuerIdValue, setIssuerIdValue] = useState<string>('')

  // Dropdown options
  const currencies = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD']

  // Load initial funds data with pagination
  const loadInitialFunds = async () => {
    setLoading(true)
    setError(null)
    setIsInitialLoad(true)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      // First get total count
      const countResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/?count_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!countResponse.ok) {
        throw new Error('Failed to get funds count')
      }
      
      const countData = await countResponse.json()
      setTotalCount(countData.total_count)
      
      // Load first 100 records immediately
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/?page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to load funds')
      }
      
      const data: PaginatedResponse = await response.json()
      
      // Convert date strings to Date objects
      const processedFunds = data.data.map(fund => ({
        ...fund,
        End_Inv_Period: fund.End_Inv_Period ? new Date(fund.End_Inv_Period) : null,
        Harvest_Period: fund.Harvest_Period ? new Date(fund.Harvest_Period) : null,
        Extension: fund.Extension ? new Date(fund.Extension) : null,
        Asof: fund.Asof ? new Date(fund.Asof) : null,
        Timestamp: new Date(fund.Timestamp)
      }))
      
      setFunds(processedFunds)
      setLoadedCount(processedFunds.length)
      setCurrentBackendPage(1)
      setHasMoreData(data.pagination.has_next)
      
      // Start background loading if there's more data
      if (data.pagination.has_next && countData.total_count > 100) {
        setTimeout(() => loadMoreFundsInBackground(countData.total_count), 1000)
      }
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load funds')
      // For development, show sample data
      const sampleFunds = [
        {
          Id: 1,
          Fund: 'Sample Fund I',
          Issuer: 'Sample Capital',
          Manager_Name: 'John Smith',
          Strategy: 'Private Equity',
          Fund_Type: 'Offshore LP',
          Currency: 'USD',
          Management_Fee_Com: 2.0,
          Management_Fee_Inv: 1.5,
          Carry: 20.0,
          Preferred_Return: 8.0,
          End_Inv_Period: new Date('2025-12-31'),
          Investment_Period: '5 years',
          Harvest_Period: new Date('2030-12-31'),
          Extension: new Date('2032-12-31'),
          Total_Fund_Committment: 100000000,
          Fund_Size: 150000000,
          Asof: new Date(),
          Timestamp: new Date(),
          Deleted: false,
          CreatedBy: user?.username || user?.homeAccountId || 'system',
          ModifiedBy: user?.username || user?.homeAccountId || 'system'
        }
      ]
      setFunds(sampleFunds)
      setTotalCount(1)
      setLoadedCount(1)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }

  // Load all remaining funds in background
  const loadMoreFundsInBackground = async (totalRecords: number) => {
    if (!hasMoreData || backgroundLoading) return
    
    setBackgroundLoading(true)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        return
      }
      
      // Calculate how many records remain
      const remainingRecords = totalRecords - loadedCount
      if (remainingRecords <= 0) {
        setHasMoreData(false)
        return
      }
      
      // Load all remaining records in one request using page=1 with a large limit
      // This ensures we get all records from the beginning, then we'll filter out duplicates
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/?page=1&limit=${totalRecords}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        return
      }
      
      const data: PaginatedResponse = await response.json()
      
      // Convert date strings to Date objects
      const processedFunds = data.data.map(fund => ({
        ...fund,
        End_Inv_Period: fund.End_Inv_Period ? new Date(fund.End_Inv_Period) : null,
        Harvest_Period: fund.Harvest_Period ? new Date(fund.Harvest_Period) : null,
        Extension: fund.Extension ? new Date(fund.Extension) : null,
        Asof: fund.Asof ? new Date(fund.Asof) : null,
        Timestamp: new Date(fund.Timestamp)
      }))
      
      // Replace all funds with the complete dataset
      setFunds(processedFunds)
      setLoadedCount(processedFunds.length)
      setHasMoreData(false) // All data loaded
      
    } catch (err) {
      // Silently fail background loading
      console.warn('Background loading failed:', err)
    } finally {
      setBackgroundLoading(false)
    }
  }

  // Load strategies from database and combine with local fund data
  const loadStrategies = async () => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        // Fallback to default strategies if no token
        setStrategies(['Private Equity', 'Hedge Fund', 'Real Estate', 'Infrastructure', 'Credit', 'Venture Capital'])
        return
      }
      
      // Load strategies from database
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/strategies`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const dbStrategies = await response.json()
        
        // Get strategies from currently loaded funds
        const localStrategies = [...new Set(funds
          .map(fund => fund.Strategy)
          .filter(strategy => strategy && strategy.trim())
        )]
        
        // Combine database strategies with local strategies and default fallbacks
        const defaultStrategies = ['Private Equity', 'Hedge Fund', 'Real Estate', 'Infrastructure', 'Credit', 'Venture Capital']
        const allStrategies = [...new Set([...dbStrategies, ...localStrategies, ...defaultStrategies])]
        
        // Sort alphabetically
        allStrategies.sort()
        
        setStrategies(allStrategies)
      } else {
        // Fallback to local strategies + defaults
        const localStrategies = [...new Set(funds
          .map(fund => fund.Strategy)
          .filter(strategy => strategy && strategy.trim())
        )]
        const defaultStrategies = ['Private Equity', 'Hedge Fund', 'Real Estate', 'Infrastructure', 'Credit', 'Venture Capital']
        const allStrategies = [...new Set([...localStrategies, ...defaultStrategies])]
        allStrategies.sort()
        setStrategies(allStrategies)
      }
    } catch (err) {
      console.error('Error loading strategies:', err)
      // Fallback to defaults
      setStrategies(['Private Equity', 'Hedge Fund', 'Real Estate', 'Infrastructure', 'Credit', 'Venture Capital'])
    }
  }

  // Load fund types from database and combine with local fund data
  const loadFundTypes = async () => {
    try {
      const token = await getAccessToken()
      
      if (!token) {
        // Fallback to default fund types if no token
        setFundTypes(['Offshore LP', 'Onshore LP', 'Offshore Ltd.', 'Unit Trust'])
        return
      }
      
      // Load fund types from database
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const dbFundTypes = await response.json()
        
        // Get fund types from currently loaded funds
        const localFundTypes = [...new Set(funds
          .map(fund => fund.Fund_Type)
          .filter(type => type && type.trim())
        )]
        
        // Combine database fund types with local fund types and default fallbacks
        const defaultFundTypes = ['Offshore LP', 'Onshore LP', 'Offshore Ltd.', 'Unit Trust']
        const allFundTypes = [...new Set([...dbFundTypes, ...localFundTypes, ...defaultFundTypes])]
        
        // Sort alphabetically
        allFundTypes.sort()
        
        setFundTypes(allFundTypes)
      } else {
        // Fallback to local fund types + defaults
        const localFundTypes = [...new Set(funds
          .map(fund => fund.Fund_Type)
          .filter(type => type && type.trim())
        )]
        const defaultFundTypes = ['Offshore LP', 'Onshore LP', 'Offshore Ltd.', 'Unit Trust']
        const allFundTypes = [...new Set([...localFundTypes, ...defaultFundTypes])]
        allFundTypes.sort()
        setFundTypes(allFundTypes)
      }
    } catch (err) {
      console.error('Error loading fund types:', err)
      // Fallback to defaults
      setFundTypes(['Offshore LP', 'Onshore LP', 'Offshore Ltd.', 'Unit Trust'])
    }
  }

  // Refresh all data
  const refreshFunds = () => {
    setFunds([])
    setTotalCount(0)
    setLoadedCount(0)
    setCurrentBackendPage(1)
    setHasMoreData(true)
    loadInitialFunds()
  }

  useEffect(() => {
    loadInitialFunds()
    loadStrategies()
    loadFundTypes()
  }, [])

  // Update strategies and fund types when funds data changes
  useEffect(() => {
    if (funds.length > 0) {
      loadStrategies()
      loadFundTypes()
    }
  }, [funds])


  // Handle form changes
  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    setSuccess(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const url = editingId 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/${editingId}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/`
      
      const method = editingId ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          CreatedBy: editingId ? undefined : user?.username || user?.homeAccountId || 'system',
          ModifiedBy: user?.username || user?.homeAccountId || 'system'
        })
      })
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingId ? 'update' : 'create'} fund`)
      }
      
      const result = await response.json()
      const updatedFund = result.fund
      
      if (editingId) {
        // Update existing fund in the list
        setFunds(prevFunds => prevFunds.map(fund => 
          fund.Id === editingId ? {
            ...updatedFund,
            End_Inv_Period: updatedFund.End_Inv_Period ? new Date(updatedFund.End_Inv_Period) : null,
            Harvest_Period: updatedFund.Harvest_Period ? new Date(updatedFund.Harvest_Period) : null,
            Extension: updatedFund.Extension ? new Date(updatedFund.Extension) : null,
            Asof: updatedFund.Asof ? new Date(updatedFund.Asof) : null,
            Timestamp: new Date(updatedFund.Timestamp)
          } : fund
        ))
      } else {
        // Add new fund to the list
        const newFund = {
          ...updatedFund,
          End_Inv_Period: updatedFund.End_Inv_Period ? new Date(updatedFund.End_Inv_Period) : null,
          Harvest_Period: updatedFund.Harvest_Period ? new Date(updatedFund.Harvest_Period) : null,
          Extension: updatedFund.Extension ? new Date(updatedFund.Extension) : null,
          Asof: updatedFund.Asof ? new Date(updatedFund.Asof) : null,
          Timestamp: new Date(updatedFund.Timestamp)
        }
        setFunds(prevFunds => [newFund, ...prevFunds])
        setTotalCount(prevCount => prevCount + 1)
        setLoadedCount(prevCount => prevCount + 1)
      }
      
      // Save identifiers if any are filled in
      const identifiersToSave: Array<{entityType: string, idType: string, idValue: string}> = []
      if (fundIdType && fundIdValue) {
        identifiersToSave.push({ entityType: 'Fund', idType: fundIdType, idValue: fundIdValue })
      }
      if (issuerIdType && issuerIdValue) {
        identifiersToSave.push({ entityType: 'Issuer', idType: issuerIdType, idValue: issuerIdValue })
      }

      const identifierWarnings: string[] = []
      for (const id of identifiersToSave) {
        try {
          const idResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              Entity_Type: id.entityType,
              Entity_Name: formData.Fund,
              Identifier_Type: id.idType,
              Identifier_Value: id.idValue,
              Asof: formData.Asof ? (formData.Asof instanceof Date ? formData.Asof.toISOString() : formData.Asof) : null,
              Deleted: formData.Deleted ? 1 : 0
            })
          })
          if (!idResponse.ok) {
            const errData = await idResponse.json().catch(() => ({}))
            identifierWarnings.push(errData.detail || `Failed to save ${id.entityType} identifier`)
          }
        } catch (idErr) {
          identifierWarnings.push(`Failed to save ${id.entityType} identifier`)
        }
      }

      const baseMsg = `Fund ${editingId ? 'updated' : 'created'} successfully`
      if (identifierWarnings.length > 0) {
        setSuccess(baseMsg)
        setError(`Identifier warnings: ${identifierWarnings.join('; ')}`)
      } else {
        setSuccess(baseMsg + (identifiersToSave.length > 0 ? ` with ${identifiersToSave.length} identifier(s)` : ''))
      }
      handleClear()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle edit (update existing fund entry)
  const handleEdit = async (fund: Fund) => {
    try {
      // Convert date strings to Date objects if they're strings
      const parseDate = (dateValue: any): Date | null => {
        if (!dateValue) return null
        if (dateValue instanceof Date) return dateValue
        if (typeof dateValue === 'string') {
          const parsed = new Date(dateValue)
          return isNaN(parsed.getTime()) ? null : parsed
        }
        return null
      }

      setFormData({
        Fund: fund.Fund || '',
        Issuer: fund.Issuer || '',
        Manager_Name: fund.Manager_Name || '',
        Strategy: fund.Strategy || '',
        Fund_Type: fund.Fund_Type || '',
        Currency: fund.Currency || '',
        Management_Fee_Com: fund.Management_Fee_Com,
        Management_Fee_Inv: fund.Management_Fee_Inv,
        Carry: fund.Carry,
        Preferred_Return: fund.Preferred_Return,
        End_Inv_Period: parseDate(fund.End_Inv_Period),
        Investment_Period: fund.Investment_Period || '',
        Harvest_Period: parseDate(fund.Harvest_Period),
        Extension: parseDate(fund.Extension),
        Total_Fund_Committment: fund.Total_Fund_Committment,
        Fund_Size: fund.Fund_Size,
        Asof: parseDate(fund.Asof),
        Deleted: fund.Deleted || false
      })
      setEditingId(fund.Id)

      // Reset identifier fields immediately before async load
      setFundIdType('')
      setFundIdValue('')
      setIssuerIdType('')
      setIssuerIdValue('')

      // Load existing identifiers for this fund
      try {
        const token = await getAccessToken()
        if (token && fund.Fund) {
          const loadIds = async (entityType: string) => {
            const resp = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/by-entity?entity_type=${encodeURIComponent(entityType)}&entity_name=${encodeURIComponent(fund.Fund)}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            )
            if (resp.ok) return await resp.json()
            return []
          }
          const [fundIds, issuerIds] = await Promise.all([loadIds('Fund'), loadIds('Issuer')])
          if (fundIds.length > 0) {
            setFundIdType(fundIds[0].Identifier_Name || '')
            setFundIdValue(fundIds[0].Identifier_Value || '')
          } else {
            setFundIdType('')
            setFundIdValue('')
          }
          if (issuerIds.length > 0) {
            setIssuerIdType(issuerIds[0].Identifier_Name || '')
            setIssuerIdValue(issuerIds[0].Identifier_Value || '')
          } else {
            setIssuerIdType('')
            setIssuerIdValue('')
          }
        }
      } catch (idErr) {
        console.error('Failed to load identifiers:', idErr)
      }

      // Scroll to the form section to make it visible
      const formElement = document.querySelector('[data-form-section]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      console.error('Error in handleEdit:', error)
      setError('Failed to load fund data for editing')
    }
  }

  // Handle copy as new (create new historical entry with fund data)
  const handleCopyAsNew = async (fund: Fund) => {
    try {
      // Convert date strings to Date objects if they're strings
      const parseDate = (dateValue: any): Date | null => {
        if (!dateValue) return null
        if (dateValue instanceof Date) return dateValue
        if (typeof dateValue === 'string') {
          const parsed = new Date(dateValue)
          return isNaN(parsed.getTime()) ? null : parsed
        }
        return null
      }

      setFormData({
        Fund: fund.Fund || '',
        Issuer: fund.Issuer || '',
        Manager_Name: fund.Manager_Name || '',
        Strategy: fund.Strategy || '',
        Fund_Type: fund.Fund_Type || '',
        Currency: fund.Currency || '',
        Management_Fee_Com: fund.Management_Fee_Com,
        Management_Fee_Inv: fund.Management_Fee_Inv,
        Carry: fund.Carry,
        Preferred_Return: fund.Preferred_Return,
        End_Inv_Period: parseDate(fund.End_Inv_Period),
        Investment_Period: fund.Investment_Period || '',
        Harvest_Period: parseDate(fund.Harvest_Period),
        Extension: parseDate(fund.Extension),
        Total_Fund_Committment: fund.Total_Fund_Committment,
        Fund_Size: fund.Fund_Size,
        Asof: new Date(), // Set to today for new historical entry
        Deleted: false
      })
      setEditingId(null) // Clear editing ID to indicate this is a new entry

      // Reset identifier fields immediately before async load
      setFundIdType('')
      setFundIdValue('')
      setIssuerIdType('')
      setIssuerIdValue('')

      // Load existing identifiers for this fund (same as handleEdit)
      try {
        const token = await getAccessToken()
        if (token && fund.Fund) {
          const loadIds = async (entityType: string) => {
            const resp = await fetch(
              `${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/by-entity?entity_type=${encodeURIComponent(entityType)}&entity_name=${encodeURIComponent(fund.Fund)}`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            )
            if (resp.ok) return await resp.json()
            return []
          }
          const [fundIds, issuerIds] = await Promise.all([loadIds('Fund'), loadIds('Issuer')])
          if (fundIds.length > 0) {
            setFundIdType(fundIds[0].Identifier_Name || '')
            setFundIdValue(fundIds[0].Identifier_Value || '')
          }
          if (issuerIds.length > 0) {
            setIssuerIdType(issuerIds[0].Identifier_Name || '')
            setIssuerIdValue(issuerIds[0].Identifier_Value || '')
          }
        }
      } catch (idErr) {
        console.error('Failed to load identifiers:', idErr)
      }

      setSuccess(`Form pre-filled with data from "${fund.Fund}". Update As Of date and Fund Size, then click Submit to create new entry.`)

      // Scroll to the form section to make it visible
      const formElement = document.querySelector('[data-form-section]')
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      console.error('Error in handleCopyAsNew:', error)
      setError('Failed to load fund data for copying')
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (!fundToDelete) return
    
    setLoading(true)
    setError(null)
    
    try {
      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/funds/${fundToDelete.Id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete fund')
      }
      
      setSuccess('Fund deleted successfully')
      setDeleteDialogOpen(false)
      setFundToDelete(null)
      refreshFunds()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setLoading(false)
    }
  }

  // Handle clear form
  const handleClear = () => {
    setFormData(initialFund)
    setAppliedFilters(initialFund)
    setEditingId(null)
    setFundIdType('')
    setFundIdValue('')
    setIssuerIdType('')
    setIssuerIdValue('')
  }

  // Handle search button click
  const handleSearch = () => {
    setAppliedFilters({ ...formData })
    setPage(0) // Reset pagination when filtering
  }

  // Check if any filter criteria are set in form
  const hasFilterCriteria = formData.Fund || formData.Issuer || formData.Manager_Name || 
    formData.Strategy || formData.Fund_Type || formData.Currency || 
    formData.Management_Fee_Com !== null || formData.Management_Fee_Inv !== null ||
    formData.Carry !== null || formData.Preferred_Return !== null ||
    formData.End_Inv_Period || formData.Investment_Period ||
    formData.Harvest_Period || formData.Extension || formData.Fund_Size !== null ||
    formData.Asof

  // Check if any filters are currently applied
  const hasAppliedFilters = appliedFilters.Fund || appliedFilters.Issuer || appliedFilters.Manager_Name || 
    appliedFilters.Strategy || appliedFilters.Fund_Type || appliedFilters.Currency || 
    appliedFilters.Management_Fee_Com !== null || appliedFilters.Management_Fee_Inv !== null ||
    appliedFilters.Carry !== null || appliedFilters.Preferred_Return !== null ||
    appliedFilters.End_Inv_Period || appliedFilters.Investment_Period ||
    appliedFilters.Harvest_Period || appliedFilters.Extension || appliedFilters.Fund_Size !== null ||
    appliedFilters.Asof

  // Filter funds
  const filteredFunds = funds.filter(fund => {
    // Apply form-based filters only if they have been applied via search button
    const matchesFormFilters = !hasAppliedFilters || (
      (!appliedFilters.Fund || fund.Fund.toLowerCase().includes(appliedFilters.Fund.toLowerCase())) &&
      (!appliedFilters.Issuer || fund.Issuer.toLowerCase().includes(appliedFilters.Issuer.toLowerCase())) &&
      (!appliedFilters.Manager_Name || fund.Manager_Name.toLowerCase().includes(appliedFilters.Manager_Name.toLowerCase())) &&
      (!appliedFilters.Strategy || fund.Strategy === appliedFilters.Strategy) &&
      (!appliedFilters.Fund_Type || fund.Fund_Type === appliedFilters.Fund_Type) &&
      (!appliedFilters.Currency || fund.Currency === appliedFilters.Currency) &&
      (appliedFilters.Management_Fee_Com === null || fund.Management_Fee_Com === appliedFilters.Management_Fee_Com) &&
      (appliedFilters.Management_Fee_Inv === null || fund.Management_Fee_Inv === appliedFilters.Management_Fee_Inv) &&
      (appliedFilters.Carry === null || fund.Carry === appliedFilters.Carry) &&
      (appliedFilters.Preferred_Return === null || fund.Preferred_Return === appliedFilters.Preferred_Return) &&
      (!appliedFilters.End_Inv_Period || (fund.End_Inv_Period && fund.End_Inv_Period.toDateString() === appliedFilters.End_Inv_Period.toDateString())) &&
      (!appliedFilters.Investment_Period || fund.Investment_Period.toLowerCase().includes(appliedFilters.Investment_Period.toLowerCase())) &&
      (!appliedFilters.Harvest_Period || (fund.Harvest_Period && fund.Harvest_Period.toDateString() === appliedFilters.Harvest_Period.toDateString())) &&
      (!appliedFilters.Extension || (fund.Extension && fund.Extension.toDateString() === appliedFilters.Extension.toDateString())) &&
      (appliedFilters.Fund_Size === null || fund.Fund_Size === appliedFilters.Fund_Size) &&
      (!appliedFilters.Asof || (fund.Asof && fund.Asof.toDateString() === appliedFilters.Asof.toDateString()))
    )
    
    return matchesFormFilters
  })

  // Apply sorting to filtered funds
  const { sortedData: sortedFunds, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredFunds,
    initialSort: [{ field: 'Fund', direction: 'asc' }]
  })

  // Paginated funds
  const paginatedFunds = sortedFunds.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
          Funds Management
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }} data-form-section>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="h6">
                  {editingId ? 'Edit Fund Entry' : hasFilterCriteria ? 'Add New Fund / Search Filters' : 'Add New Fund Entry'}
                </Typography>
              </Box>
              {editingId && (
                <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    You are updating an existing fund entry. To create a new historical entry instead, use the "Copy as New" button from the table.
                  </Typography>
                </Alert>
              )}
              {hasFilterCriteria && !editingId && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  <Typography variant="body2">
                    Form fields are being used to filter the funds list. Click "Search" to apply filters or "Clear" to reset.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={1.5}>
                {/* Basic Information */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Fund Name *"
                    value={formData.Fund}
                    onChange={(e) => handleInputChange('Fund', e.target.value)}
                    fullWidth
                    required
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Issuer"
                    value={formData.Issuer}
                    onChange={(e) => handleInputChange('Issuer', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Manager Name"
                    value={formData.Manager_Name}
                    onChange={(e) => handleInputChange('Manager_Name', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Strategy"
                    value={formData.Strategy}
                    onChange={(e) => handleInputChange('Strategy', e.target.value)}
                    fullWidth
                    size="small"
                    select
                    SelectProps={{
                      native: false,
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      },
                    }}
                  >
                    {strategies.map(strategy => (
                      <MenuItem key={strategy} value={strategy}>
                        {strategy}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Fund Type"
                    value={formData.Fund_Type}
                    onChange={(e) => handleInputChange('Fund_Type', e.target.value)}
                    fullWidth
                    size="small"
                    select
                    SelectProps={{
                      native: false,
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                          },
                        },
                      },
                    }}
                  >
                    {fundTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={formData.Currency}
                      onChange={(e) => handleInputChange('Currency', e.target.value)}
                      label="Currency"
                    >
                      {currencies.map(currency => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Fund Size"
                    type="number"
                    value={formData.Fund_Size !== null && formData.Fund_Size !== undefined ? formData.Fund_Size : ''}
                    onChange={(e) => handleInputChange('Fund_Size', e.target.value ? parseFloat(e.target.value) : null)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="As Of Date *"
                    value={formData.Asof}
                    onChange={(date) => handleInputChange('Asof', date)}
                    slotProps={{ 
                      textField: { 
                        fullWidth: true,
                        size: 'small'
                      } 
                    }}
                  />
                </Grid>

                {/* Fees & Returns - Collapsible Section */}
                <Grid item xs={12}>
                  <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}>
                      <Typography variant="body2" fontWeight={500}>Fees & Returns</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={4} md={3}>
                          <TextField
                            label="Mgmt Fee - Committed (%)"
                            type="number"
                            value={formData.Management_Fee_Com || ''}
                            onChange={(e) => handleInputChange('Management_Fee_Com', e.target.value ? parseFloat(e.target.value) : null)}
                            fullWidth
                            size="small"
                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                          />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <TextField
                            label="Mgmt Fee - Invested (%)"
                            type="number"
                            value={formData.Management_Fee_Inv || ''}
                            onChange={(e) => handleInputChange('Management_Fee_Inv', e.target.value ? parseFloat(e.target.value) : null)}
                            fullWidth
                            size="small"
                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                          />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <TextField
                            label="Carry (%)"
                            type="number"
                            value={formData.Carry || ''}
                            onChange={(e) => handleInputChange('Carry', e.target.value ? parseFloat(e.target.value) : null)}
                            fullWidth
                            size="small"
                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                          />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <TextField
                            label="Preferred Return (%)"
                            type="number"
                            value={formData.Preferred_Return || ''}
                            onChange={(e) => handleInputChange('Preferred_Return', e.target.value ? parseFloat(e.target.value) : null)}
                            fullWidth
                            size="small"
                            inputProps={{ step: 0.1, min: 0, max: 100 }}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {/* Investment Timeline - Collapsible Section */}
                <Grid item xs={12}>
                  <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}>
                      <Typography variant="body2" fontWeight={500}>Investment Timeline</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={4} md={3}>
                          <DatePicker
                            label="Investment Period End"
                            value={formData.End_Inv_Period}
                            onChange={(date) => handleInputChange('End_Inv_Period', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <TextField
                            label="Investment Period"
                            value={formData.Investment_Period}
                            onChange={(e) => handleInputChange('Investment_Period', e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g., 5 years"
                          />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <DatePicker
                            label="Harvest Period End"
                            value={formData.Harvest_Period}
                            onChange={(date) => handleInputChange('Harvest_Period', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>

                        <Grid item xs={6} sm={4} md={3}>
                          <DatePicker
                            label="Final Extension Date"
                            value={formData.Extension}
                            onChange={(date) => handleInputChange('Extension', date)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {/* Identifiers Section */}
                <Grid item xs={12}>
                  <Accordion defaultExpanded={false} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}>
                      <Typography variant="body2" fontWeight={500}>Identifiers</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 1 }}>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Fund Identifier</InputLabel>
                            <Select
                              value={fundIdType}
                              onChange={(e) => setFundIdType(e.target.value)}
                              label="Fund Identifier"
                            >
                              <MenuItem value="">None</MenuItem>
                              {IDENTIFIER_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Fund Identifier Value"
                            value={fundIdValue}
                            onChange={(e) => setFundIdValue(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={!fundIdType}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Issuer Identifier</InputLabel>
                            <Select
                              value={issuerIdType}
                              onChange={(e) => setIssuerIdType(e.target.value)}
                              label="Issuer Identifier"
                            >
                              <MenuItem value="">None</MenuItem>
                              {IDENTIFIER_TYPES.map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <TextField
                            label="Issuer Identifier Value"
                            value={issuerIdValue}
                            onChange={(e) => setIssuerIdValue(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={!issuerIdType}
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.Deleted}
                        onChange={(e) => handleInputChange('Deleted', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Deleted"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading || !formData.Fund}
                    sx={{ flex: hasFilterCriteria && !editingId ? 1 : 2 }}
                  >
                    {loading ? 'Processing...' : (editingId ? 'Update Entry' : 'Submit New Entry')}
                  </Button>
                  {hasFilterCriteria && !editingId && (
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={loading}
                      sx={{ flex: 1 }}
                    >
                      Search
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleClear}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Table Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6">
                    Funds List ({filteredFunds.length} of {totalCount})
                  </Typography>
                  {hasAppliedFilters && (
                    <Typography variant="body2" color="text.secondary">
                      Filtered by form criteria
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton onClick={refreshFunds} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableHeader
                        field="Fund"
                        label="Fund Name"
                        sortDirection={getSortDirection('Fund')}
                        sortIndex={getSortIndex('Fund')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Issuer"
                        label="Issuer"
                        sortDirection={getSortDirection('Issuer')}
                        sortIndex={getSortIndex('Issuer')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Strategy"
                        label="Strategy"
                        sortDirection={getSortDirection('Strategy')}
                        sortIndex={getSortIndex('Strategy')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Fund_Type"
                        label="Type"
                        sortDirection={getSortDirection('Fund_Type')}
                        sortIndex={getSortIndex('Fund_Type')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Currency"
                        label="Currency"
                        sortDirection={getSortDirection('Currency')}
                        sortIndex={getSortIndex('Currency')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Fund_Size"
                        label="Fund Size"
                        sortDirection={getSortDirection('Fund_Size')}
                        sortIndex={getSortIndex('Fund_Size')}
                        onSort={handleSort}
                        align="right"
                      />
                      <SortableTableHeader
                        field="Asof"
                        label="As Of Date"
                        sortDirection={getSortDirection('Asof')}
                        sortIndex={getSortIndex('Asof')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Timestamp"
                        label="Timestamp"
                        sortDirection={getSortDirection('Timestamp')}
                        sortIndex={getSortIndex('Timestamp')}
                        onSort={handleSort}
                      />
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && funds.length === 0 ? (
                      // Show skeleton rows when loading initial data
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="40%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="50%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Skeleton variant="circular" width={24} height={24} />
                              <Skeleton variant="circular" width={24} height={24} />
                              <Skeleton variant="circular" width={24} height={24} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : paginatedFunds.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No funds found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedFunds.map((fund) => (
                        <TableRow key={fund.Id}>
                          <TableCell>{fund.Fund}</TableCell>
                          <TableCell>{fund.Issuer}</TableCell>
                          <TableCell>{fund.Strategy}</TableCell>
                          <TableCell>{fund.Fund_Type}</TableCell>
                          <TableCell>{fund.Currency}</TableCell>
                          <TableCell align="right">
                            {fund.Fund_Size !== null && fund.Fund_Size !== undefined ? fund.Fund_Size.toLocaleString() : '-'}
                          </TableCell>
                          <TableCell>
                            {fund.Asof ? new Date(fund.Asof).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            }) : '-'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {fund.Timestamp ? new Date(fund.Timestamp).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Copy as New Entry">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyAsNew(fund)}
                                  disabled={loading}
                                  color="primary"
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit This Entry">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(fund)}
                                  disabled={loading}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setFundToDelete(fund)
                                    setDeleteDialogOpen(true)
                                  }}
                                  disabled={loading}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredFunds.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10))
                  setPage(0)
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the fund "{fundToDelete?.Fund}"?
              This action will mark the fund as deleted but preserve the data for audit purposes.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" disabled={loading}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default FundsManagement